import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, ArrowUpRight, Phone, Check } from "lucide-react";
import { SERVICES, CATEGORIES, PRACTICE, ASSETS } from "@/data/site";
import { useT } from "@/i18n/LanguageContext";
import { localizeService, localizeCategory } from "@/i18n/data.es";
import SunDivider from "@/components/site/SunDivider";
import ContactForm from "@/components/site/ContactForm";
import Seo from "@/components/site/Seo";
import { serviceSchema, faqSchema, breadcrumbSchema } from "@/lib/seo";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const { t, lang } = useT();
  const raw = SERVICES.find((s) => s.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (!raw) return <Navigate to="/services" replace />;

  const service = localizeService(raw, lang);
  const categoryRaw = CATEGORIES.find((c) => c.slug === raw.category);
  const category = categoryRaw ? localizeCategory(categoryRaw, lang) : null;
  const related = SERVICES.filter(
    (s) => s.category === raw.category && s.slug !== raw.slug,
  )
    .slice(0, 3)
    .map((s) => localizeService(s, lang));

  return (
    <article data-testid={`service-page-${service.slug}`}>
      <Seo
        title={`${service.title} in Leesburg, VA`}
        description={service.short || `${service.title} at Smiles of Loudoun in Leesburg, VA.`}
        path={`/services/${service.slug}`}
        jsonLd={[
          serviceSchema(raw),
          Array.isArray(raw.faq) && raw.faq.length > 0 ? faqSchema(raw.faq) : null,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            { name: service.title, path: `/services/${service.slug}` },
          ]),
        ].filter(Boolean)}
      />
      {/* Hero */}
      <section className="px-6 md:px-10 lg:px-16 pt-14 md:pt-20 pb-12">
        <Link
          to="/services"
          className="font-mono-eyebrow text-[var(--text-muted)] inline-flex items-center gap-2 hover:text-[var(--accent)]"
        >
          ← All services
        </Link>
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 mt-6 items-end">
          <div className="lg:col-span-7">
            <div className="font-mono-eyebrow text-[var(--accent)]">
              {service.tag}
            </div>
            <h1 className="font-serif-display text-[var(--primary)] mt-4 text-[56px] md:text-[88px] leading-[0.98] tracking-tight">
              {service.title}
            </h1>
            <SunDivider className="mt-6 !justify-start" />
            <p className="mt-5 max-w-xl text-[18px] text-[var(--text-muted)] leading-relaxed">
              {service.long?.[0]}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={PRACTICE.appointmentUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="service-appointment-btn"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--primary)] text-[var(--bg)] hover:bg-[var(--primary-dark)]"
              >
                Request Consult <ArrowUpRight className="w-4 h-4" />
              </a>
              <a
                href={PRACTICE.phoneHref}
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-[var(--bg)]"
              >
                <Phone className="w-4 h-4" /> {PRACTICE.phone}
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-3xl bg-[var(--primary)] grid place-content-center mx-auto lg:ml-auto">
              <img
                src={ASSETS.serviceIcons[service.icon]}
                alt=""
                className="w-16 h-16 md:w-20 md:h-20 object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Body content */}
      <section className="px-6 md:px-10 lg:px-16 py-12 md:py-16 bg-[var(--surface)]/60 border-y border-[var(--border)]">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-7 space-y-6">
            {service.long?.slice(1).map((p, i) => (
              <p
                key={i}
                className="text-[17px] leading-relaxed text-[var(--text-main)]/85"
              >
                {p}
              </p>
            ))}
            {service.bullets && (
              <div className="bg-[var(--bg)] border border-[var(--border)] rounded-3xl p-7 md:p-8 mt-8">
                <h3 className="font-serif-display text-[28px] text-[var(--primary)]">
                  {t("Why patients love")} {service.title}
                </h3>
                <ul className="mt-5 space-y-3.5">
                  {service.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-3 text-[var(--text-main)]/85"
                    >
                      <Check
                        className="w-5 h-5 text-[var(--accent)] mt-0.5 flex-shrink-0"
                        strokeWidth={2}
                      />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-5 lg:sticky lg:top-28 self-start">
            <div className="bg-[var(--primary)] text-[var(--bg)] rounded-3xl p-8">
              <div className="font-mono-eyebrow text-[var(--accent)]">
                {t("New patient special")}
              </div>
              <div className="font-serif-display text-[64px] leading-none mt-3">
                <span className="italic text-[var(--accent)]">$</span>129
              </div>
              <p className="text-[var(--bg)]/80 mt-2">
                {t("Exam · X-Rays · Cleaning")}
              </p>
              <a
                href={PRACTICE.appointmentUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[var(--accent)] text-[var(--primary)] font-medium hover:bg-[var(--accent-hover)] hover:text-[var(--bg)]"
              >
                {t("Claim Offer")} <ArrowUpRight className="w-4 h-4" />
              </a>
              <div className="mt-8 pt-6 border-t border-[var(--bg)]/15 text-sm space-y-2 text-[var(--bg)]/80">
                <div>📍 {PRACTICE.address}</div>
                <div>📞 {PRACTICE.phone}</div>
                <div>🗣 {PRACTICE.languages.join(" · ")}</div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* FAQ */}
      {service.faq && service.faq.length > 0 && (
        <section className="px-6 md:px-10 lg:px-16 py-20 md:py-28">
          <div className="text-center mb-10">
            <div className="font-mono-eyebrow text-[var(--text-muted)]">
              {t("Frequently asked")}
            </div>
            <h2 className="font-serif-display text-[var(--primary)] mt-4 text-[40px] md:text-[56px] leading-tight tracking-tight">
              {t("Questions,")} <span className="italic text-[var(--accent)]">{t("answered")}</span>.
            </h2>
            <SunDivider className="mt-5" />
          </div>
          <div className="max-w-3xl mx-auto space-y-3">
            {service.faq.map((f, i) => (
              <FAQItem key={f.q} q={f.q} a={f.a} idx={i} />
            ))}
          </div>
        </section>
      )}

      {/* Related services */}
      {related.length > 0 && (
        <section
          data-testid={`related-services-${service.category}`}
          className="px-6 md:px-10 lg:px-16 py-16 md:py-20"
        >
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-8">
            <div>
              <div className="font-mono-eyebrow text-[var(--text-muted)]">
                {t("Related care ·")} {category?.title}
              </div>
              <h2 className="font-serif-display text-[var(--primary)] mt-3 text-[32px] md:text-[44px] leading-tight tracking-tight">
                {t("Other treatments in")} <span className="italic text-[var(--accent)]">{category?.title}</span>
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="back-to-all-services"
              className="text-xs uppercase tracking-[0.22em] text-[var(--primary)] hover:text-[var(--accent)] inline-flex items-center gap-1"
            >
              {t("View all services")} <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {related.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`related-service-${s.slug}`}
                className="group block bg-[var(--surface)] border border-[var(--border)] rounded-3xl p-7 h-full hover:bg-[var(--primary)] hover:text-[var(--bg)] hover:border-[var(--primary)] transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="w-11 h-11 rounded-full bg-[var(--primary)] grid place-content-center group-hover:bg-[var(--accent)]">
                    <img
                      src={ASSETS.serviceIcons[s.icon]}
                      alt=""
                      className="w-6 h-6 object-contain"
                      aria-hidden="true"
                    />
                  </div>
                  <ArrowUpRight className="w-5 h-5 opacity-40 group-hover:opacity-100 -rotate-12 group-hover:rotate-0 transition-all" />
                </div>
                <h3 className="font-serif-display text-[22px] mt-5 leading-tight">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm opacity-80 group-hover:opacity-90">
                  {s.short}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Contact prompt */}
      <section className="px-6 md:px-10 lg:px-16 py-20 md:py-24 bg-[var(--surface)]/60 border-t border-[var(--border)]">
        <div className="max-w-3xl mx-auto text-center mb-10">
          <div className="font-mono-eyebrow text-[var(--text-muted)]">
            {t("Ready to start?")}
          </div>
          <h2 className="font-serif-display text-[var(--primary)] mt-4 text-[40px] md:text-[56px] leading-tight tracking-tight">
            {t("Let's talk about your")} <span className="italic text-[var(--accent)]">{service.title}</span>.
          </h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <ContactForm defaultService={service.title} />
        </div>
      </section>
    </article>
  );
}

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div
      data-testid={`faq-item-${idx}`}
      className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl overflow-hidden"
    >
      <button
        data-testid={`faq-toggle-${idx}`}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 text-left px-6 py-5 text-[var(--primary)] hover:bg-[var(--accent-soft)]/40"
      >
        <span className="font-serif-display text-[20px] md:text-[22px]">{q}</span>
        <ChevronDown
          className={`w-5 h-5 flex-shrink-0 transition-transform ${
            open ? "rotate-180 text-[var(--accent)]" : ""
          }`}
        />
      </button>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="px-6 pb-5 text-[var(--text-muted)] leading-relaxed"
        >
          {a}
        </motion.div>
      )}
    </div>
  );
}
